import { z } from 'zod';

import type { LeadPhoneBook, UserInfo } from '@/providers/provider-types';

import {
  CORPORATE_APEX,
  hasCorporateApex,
  runCrmApex,
  setCorporateApex,
} from './salesforce-crm';
import { ensureSalesforceOpenCtiReady } from './salesforce-opencti';

export const parseApexString = (apexString: string) => {
  if (apexString.trim().startsWith('{"'))
    return JSON.parse(apexString) as Record<string, string>;
  const cleanString = apexString.replace(/[{}]/g, '').trim();
  const keyValuePairs = cleanString.split(', ');

  const result = keyValuePairs.reduce(
    (acc, pair) => {
      const [key, value] = pair.split('=');
      if (!key || !value) {
        return acc;
      }
      acc[key.trim()] = value.trim();
      return acc;
    },
    {} as Record<string, string>
  );

  return result;
};

const LeadPhoneBookSchema = z.record(z.string());

export const callApexForUserInfo = async (
  apexClass: string
): Promise<UserInfo> => {
  await ensureSalesforceOpenCtiReady();

  return new Promise((resolve, reject) => {
    sforce.opencti.runApex({
      apexClass,
      methodName: 'getUserInfo',
      methodParams: {},
      callback: (response) => {
        if (response.success) {
          try {
            const { runApex } = response.returnValue;
            const parsedApexString = parseApexString(runApex);

            if (!parsedApexString.email) {
              throw new Error('Email not found in user info');
            }

            resolve({ email: parsedApexString.email });
          } catch (error) {
            console.error(`Error parsing user info: ${error}`);
            reject(error);
          }
        } else {
          reject(
            new Error(
              `Error getting user info from ${apexClass}: ${response.errors}`
            )
          );
        }
      },
    });
  });
};

export const callApexForLeadPhoneBook = async (): Promise<LeadPhoneBook> => {
  await ensureSalesforceOpenCtiReady();

  if (hasCorporateApex()) {
    let cursor = '';
    const combined: LeadPhoneBook = {};
    const seen = new Set<string>();
    do {
      const page = JSON.parse(
        await runCrmApex(CORPORATE_APEX, 'getLeadPage', { afterId: cursor })
      );
      Object.assign(combined, LeadPhoneBookSchema.parse(page.phoneBook));
      cursor = page.nextCursor || '';
      if (cursor && seen.has(cursor))
        throw new Error('Salesforce agenda cursor did not advance');
      seen.add(cursor);
    } while (cursor);
    return combined;
  }
  return LeadPhoneBookSchema.parse(
    JSON.parse(await runCrmApex('LeadController', 'getLeadPhoneBook'))
  );
};

export const callCorporateUserInfo = async (): Promise<UserInfo> => {
  setCorporateApex(false);
  const info = JSON.parse(await runCrmApex(CORPORATE_APEX, 'getUserInfo'));
  if (!info.email) throw new Error('Email not found in user info');
  setCorporateApex(true);
  return { email: info.email };
};
